import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import {
  Smile, PlusCircle, Trash2, ExternalLink, Loader2,
  Package, Radio, CheckCircle2, BookMarked,
} from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { AppLayout } from '@/components/AppLayout';
import { EmojiPackCard } from '@/components/EmojiPackCard';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUserEmojiPacks, useUserEmojiList, useSavedEmojiPacks } from '@/hooks/useEmojiPacks';
import { useEmojiListManager } from '@/hooks/useEmojiListManager';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useRebroadcast } from '@/hooks/useRebroadcast';
import { useToast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import type { EmojiPack } from '@/hooks/useEmojiPacks';
import { LoginArea } from '@/components/auth/LoginArea';
import { cn } from '@/lib/utils';

export default function MyPacksPage() {
  useSeoMeta({
    title: 'My Emoji Packs — EmojiVerse',
    description: 'Manage your created and saved custom emoji packs on Nostr.',
    ogType: 'website',
    ogSiteName: 'EmojiVerse',
    ogTitle: 'My Emoji Packs — EmojiVerse',
    ogDescription: 'Manage your created and saved custom emoji packs on Nostr.',
    ogUrl: 'https://emojiverse.shakespeare.wtf/my-packs',
    ogImage: 'https://emojiverse.shakespeare.wtf/og-image.jpg',
    ogImageWidth: '1536',
    ogImageHeight: '1024',
    ogImageAlt: 'EmojiVerse — Custom Emoji Packs on Nostr',
    twitterCard: 'summary_large_image',
    twitterTitle: 'My Emoji Packs — EmojiVerse',
    twitterDescription: 'Manage your created and saved custom emoji packs on Nostr.',
    twitterImage: 'https://emojiverse.shakespeare.wtf/og-image.jpg',
    twitterImageAlt: 'EmojiVerse — Custom Emoji Packs on Nostr',
  });

  const { user } = useCurrentUser();
  const { data: myCreatedPacks, isLoading: loadingCreated } = useUserEmojiPacks(user?.pubkey);
  const { data: emojiList, isLoading: loadingList } = useUserEmojiList(user?.pubkey);
  const { data: savedPacks = [], isLoading: loadingSaved } = useSavedEmojiPacks(emojiList?.packRefs ?? []);
  const { isPackAdded, addPack, removePack } = useEmojiListManager();
  const { mutateAsync: publish } = useNostrPublish();
  const { mutate: rebroadcast, mutateAsync: rebroadcastAsync, isPending: isRebroadcasting, writeRelayCount } = useRebroadcast();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ── Created tab state ────────────────────────────────────────────────────
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [rebroadcastingId, setRebroadcastingId] = useState<string | null>(null);
  const [rebroadcastedIds, setRebroadcastedIds] = useState<Set<string>>(new Set());
  const [rebroadcastingAllCreated, setRebroadcastingAllCreated] = useState(false);

  // ── Saved tab state ──────────────────────────────────────────────────────
  const [rebroadcastingAllSaved, setRebroadcastingAllSaved] = useState(false);
  const [savedBroadcastDone, setSavedBroadcastDone] = useState(false);

  // savedPacks comes directly from useSavedEmojiPacks — resolved by exact coordinates

  // ── Handlers: Created tab ────────────────────────────────────────────────
  const handleDeletePack = async (pack: EmojiPack) => {
    setDeletingId(pack.id);
    try {
      await publish({ kind: 5, content: '', tags: [['e', pack.id]] });
      toast({ title: 'Pack deleted', description: `"${pack.title}" has been deleted.` });
      queryClient.invalidateQueries({ queryKey: ['emoji-packs', 'user', user?.pubkey] });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete pack.', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  const handleRebroadcastOne = (pack: EmojiPack) => {
    setRebroadcastingId(pack.id);
    rebroadcast({ event: pack.event }, {
      onSettled: () => setRebroadcastingId(null),
      onSuccess: ({ succeeded }) => {
        if (succeeded.length > 0) setRebroadcastedIds((prev) => new Set([...prev, pack.id]));
      },
    });
  };

  const handleRebroadcastAllCreated = async () => {
    if (!myCreatedPacks || myCreatedPacks.length === 0) return;
    setRebroadcastingAllCreated(true);
    let totalSucceeded = 0;
    let totalFailed = 0;

    for (const pack of myCreatedPacks) {
      try {
        const { succeeded, failed } = await rebroadcastAsync({ event: pack.event, silent: true });
        totalSucceeded += succeeded.length;
        totalFailed += failed.length;
        setRebroadcastedIds((prev) => new Set([...prev, pack.id]));
      } catch {
        totalFailed += writeRelayCount;
      }
    }

    setRebroadcastingAllCreated(false);
    toast({
      title: '📡 Rebroadcast complete',
      description: `${myCreatedPacks.length} pack${myCreatedPacks.length !== 1 ? 's' : ''} sent to relays. ${totalSucceeded} publish${totalSucceeded !== 1 ? 'es' : ''} succeeded${totalFailed > 0 ? `, ${totalFailed} failed` : ''}.`,
    });
  };

  // ── Handlers: Saved tab ──────────────────────────────────────────────────
  /**
   * Rebroadcast everything related to the user's saved packs:
   *  1. The kind 10030 subscription list event itself
   *  2. Each referenced kind 30030 pack event (from other authors)
   */
  const handleRebroadcastAllSaved = async () => {
    setRebroadcastingAllSaved(true);
    setSavedBroadcastDone(false);

    let totalSucceeded = 0;
    let totalFailed = 0;

    // 1. Rebroadcast the kind 10030 list event
    if (emojiList?.event) {
      try {
        const { succeeded, failed } = await rebroadcastAsync({ event: emojiList.event, silent: true });
        totalSucceeded += succeeded.length;
        totalFailed += failed.length;
      } catch {
        totalFailed += writeRelayCount;
      }
    }

    // 2. Rebroadcast each saved pack's event
    for (const pack of savedPacks) {
      try {
        const { succeeded, failed } = await rebroadcastAsync({ event: pack.event, silent: true });
        totalSucceeded += succeeded.length;
        totalFailed += failed.length;
      } catch {
        totalFailed += writeRelayCount;
      }
    }

    setRebroadcastingAllSaved(false);
    setSavedBroadcastDone(true);

    const eventCount = (emojiList?.event ? 1 : 0) + savedPacks.length;
    toast({
      title: '📡 Saved packs rebroadcast',
      description: `${eventCount} event${eventCount !== 1 ? 's' : ''} (subscription list + ${savedPacks.length} pack${savedPacks.length !== 1 ? 's' : ''}) sent to relays. ${totalSucceeded} publish${totalSucceeded !== 1 ? 'es' : ''} succeeded${totalFailed > 0 ? `, ${totalFailed} failed` : ''}.`,
    });
  };

  if (!user) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-32 text-center">
          <div className="text-7xl mb-6">🔐</div>
          <h2 className="text-2xl font-bold text-white mb-3">Sign in to manage your packs</h2>
          <p className="text-white/50 mb-8 max-w-sm">
            Log in with your Nostr account to create, manage, and add emoji packs to your profile.
          </p>
          <LoginArea className="max-w-xs" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Smile className="w-8 h-8 text-violet-400" />
            My Emoji Packs
          </h1>
          <p className="text-white/50 mt-1">Manage your created and saved emoji packs</p>
        </div>
        <Link to="/create">
          <Button className="bg-violet-500 hover:bg-violet-400 text-white rounded-full shadow-lg shadow-violet-500/30 gap-2">
            <PlusCircle className="w-4 h-4" />
            Create New
          </Button>
        </Link>
      </div>

      <Tabs defaultValue="saved" className="space-y-6">
        <TabsList className="bg-white/10 border border-white/10 rounded-xl p-1">
          <TabsTrigger
            value="saved"
            className="rounded-lg text-white/60 data-[state=active]:bg-white/15 data-[state=active]:text-white"
          >
            Saved Packs
            {emojiList && (
              <Badge className="ml-2 bg-violet-500/30 text-violet-300 border-0 text-xs">
                {emojiList.packRefs.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="created"
            className="rounded-lg text-white/60 data-[state=active]:bg-white/15 data-[state=active]:text-white"
          >
            Created by Me
            {myCreatedPacks && (
              <Badge className="ml-2 bg-fuchsia-500/30 text-fuchsia-300 border-0 text-xs">
                {myCreatedPacks.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Saved packs tab ─────────────────────────────────────────────── */}
        <TabsContent value="saved">
          {/* Rebroadcast saved banner */}
          {!loadingList && savedPacks.length > 0 && (
            <div className="flex items-center justify-between rounded-xl border border-fuchsia-500/20 bg-fuchsia-500/10 px-5 py-3.5 mb-5">
              <div className="flex items-start gap-3">
                <BookMarked className="w-4 h-4 text-fuchsia-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-white/80">Keep your subscriptions alive</p>
                  <p className="text-xs text-white/40 mt-0.5">
                    Rebroadcast your subscription list <span className="text-white/60">(kind 10030)</span> and all{' '}
                    {savedPacks.length} saved pack{savedPacks.length !== 1 ? 's' : ''}{' '}
                    to your {writeRelayCount} write {writeRelayCount === 1 ? 'relay' : 'relays'}.
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={handleRebroadcastAllSaved}
                disabled={rebroadcastingAllSaved || isRebroadcasting}
                className={cn(
                  'ml-4 shrink-0 rounded-full gap-2 text-xs transition-all',
                  savedBroadcastDone
                    ? 'bg-green-600 hover:bg-green-500 text-white'
                    : 'bg-fuchsia-500 hover:bg-fuchsia-400 text-white',
                )}
              >
                {rebroadcastingAllSaved ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" />Broadcasting…</>
                ) : savedBroadcastDone ? (
                  <><CheckCircle2 className="w-3.5 h-3.5" />Done</>
                ) : (
                  <><Radio className="w-3.5 h-3.5" />Rebroadcast All</>
                )}
              </Button>
            </div>
          )}

          {loadingList || loadingSaved ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                  <Skeleton className="h-5 w-32 bg-white/10 mb-4" />
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {Array.from({ length: 8 }).map((_, j) => (
                      <Skeleton key={j} className="aspect-square rounded-xl bg-white/10" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : savedPacks.length === 0 && !emojiList?.packRefs.length ? (
            <div className="text-center py-20 rounded-2xl border border-dashed border-white/10">
              <div className="text-6xl mb-4">📦</div>
              <p className="text-white/50 mb-4">No saved emoji packs yet</p>
              <Link to="/">
                <Button variant="outline" className="border-white/20 text-white/70 hover:bg-white/10 rounded-full gap-2">
                  <Package className="w-4 h-4" />
                  Browse Packs
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedPacks.map((pack) => (
                <EmojiPackCard
                  key={pack.id}
                  pack={pack}
                  isAdded={true}
                  onRemove={removePack}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Created packs tab ────────────────────────────────────────────── */}
        <TabsContent value="created">
          {/* Rebroadcast all created banner */}
          {myCreatedPacks && myCreatedPacks.length > 0 && (
            <div className="flex items-center justify-between rounded-xl border border-violet-500/20 bg-violet-500/10 px-5 py-3.5 mb-5">
              <div className="flex items-start gap-3">
                <Radio className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-white/80">Keep your packs alive</p>
                  <p className="text-xs text-white/40 mt-0.5">
                    Rebroadcast all {myCreatedPacks.length} pack{myCreatedPacks.length !== 1 ? 's' : ''} to your{' '}
                    {writeRelayCount} write {writeRelayCount === 1 ? 'relay' : 'relays'} so they're never lost.
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={handleRebroadcastAllCreated}
                disabled={rebroadcastingAllCreated || isRebroadcasting}
                className="ml-4 shrink-0 bg-violet-500 hover:bg-violet-400 text-white rounded-full gap-2 text-xs"
              >
                {rebroadcastingAllCreated ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" />Broadcasting…</>
                ) : (
                  <><Radio className="w-3.5 h-3.5" />Rebroadcast All</>
                )}
              </Button>
            </div>
          )}

          {loadingCreated ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                  <Skeleton className="h-5 w-32 bg-white/10 mb-4" />
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {Array.from({ length: 8 }).map((_, j) => (
                      <Skeleton key={j} className="aspect-square rounded-xl bg-white/10" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : !myCreatedPacks || myCreatedPacks.length === 0 ? (
            <div className="text-center py-20 rounded-2xl border border-dashed border-white/10">
              <div className="text-6xl mb-4">✨</div>
              <p className="text-white/50 mb-4">You haven't created any packs yet</p>
              <Link to="/create">
                <Button className="bg-violet-500 hover:bg-violet-400 text-white rounded-full gap-2">
                  <PlusCircle className="w-4 h-4" />
                  Create Your First Pack
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {myCreatedPacks.map((pack) => {
                const naddr = nip19.naddrEncode({
                  kind: 30030,
                  pubkey: pack.pubkey,
                  identifier: pack.dTag,
                });
                const isBroadcasting = rebroadcastingId === pack.id || rebroadcastingAllCreated;
                const wasBroadcasted = rebroadcastedIds.has(pack.id);

                return (
                  <div key={pack.id} className="relative">
                    <EmojiPackCard
                      pack={pack}
                      showAuthor={false}
                      isAdded={isPackAdded(pack)}
                      onAdd={addPack}
                      onRemove={removePack}
                    />
                    {/* Manage actions */}
                    <div className="flex items-center gap-2 px-5 pb-4 -mt-2">
                      <Link to={`/edit/${naddr}`} className="flex-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full rounded-xl border-white/10 text-white/60 hover:bg-white/10 hover:text-white text-xs gap-1.5"
                        >
                          Edit Pack
                        </Button>
                      </Link>

                      {/* Per-pack rebroadcast */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRebroadcastOne(pack)}
                            disabled={isBroadcasting}
                            className={cn(
                              'rounded-xl hover:bg-white/10 transition-colors',
                              wasBroadcasted
                                ? 'text-green-400 hover:text-green-300'
                                : 'text-white/40 hover:text-violet-300',
                            )}
                          >
                            {isBroadcasting ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : wasBroadcasted ? (
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            ) : (
                              <Radio className="w-3.5 h-3.5" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-gray-900 border-white/10 text-white text-xs">
                          {wasBroadcasted
                            ? `Sent to ${writeRelayCount} ${writeRelayCount === 1 ? 'relay' : 'relays'} ✓`
                            : `Rebroadcast to ${writeRelayCount} write ${writeRelayCount === 1 ? 'relay' : 'relays'}`}
                        </TooltipContent>
                      </Tooltip>

                      <Link to={`/${naddr}`}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-xl text-white/40 hover:text-white/70 hover:bg-white/10"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </Link>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl text-white/40 hover:text-red-400 hover:bg-red-500/10"
                            disabled={deletingId === pack.id}
                          >
                            {deletingId === pack.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-gray-900 border-white/10 text-white">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete "{pack.title}"?</AlertDialogTitle>
                            <AlertDialogDescription className="text-white/50">
                              This will publish a deletion request for this emoji pack. The pack may still be visible on some relays.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-white/10 border-white/10 text-white hover:bg-white/20">
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeletePack(pack)}
                              className="bg-red-600 hover:bg-red-500 text-white"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}
